import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { css, styled, up } from "@xstyled/styled-components";

import BetterMatchesImage from "./BetterMatchesImage.jpg";
import ClearerConversations from "./ClearerConversations.png";
import FasterEverything from "./FasterEverything.png";
import GetApproached from "./GetApproached.png";
import JobsThatFindYou from "./JobsThatFindYou.png";
import MoreTransparency from "./MoreTransparency.png";
import Preview from "./Preview.svg";

import { pushAnalyticsEvent } from "@otta/analytics";
import { Button, Text, WttjLogo } from "@otta/design";
import { palette } from "@otta/design-tokens";

const MOBILE_WIDE = 426;
const upMobileWide = (styles: ReturnType<typeof css>) => css`
  @media (min-width: ${MOBILE_WIDE}px) {
    ${styles}
  }
`;

const PageWrapper = styled.div`
  background-color: ${palette.brand.beige};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 lg;
`;

const HeaderWrapper = styled.div`
  align-items: center;
  background-color: ${palette.brand.yellow};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  margin: lg 0 xxl 0;
  max-width: 1100px;
  padding: lg lg xxl lg;
  width: 100%;
  ${upMobileWide(css`
    padding: lg lg ${"4xl"} lg;
  `)}
`;

const StyledWttjLogo = styled(WttjLogo)`
  margin-bottom: xl;
  width: 150px;
  ${upMobileWide(css`
    margin-bottom: xxl;
  `)}
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  ${up(
    "tablet",
    css`
      flex-direction: row;
    `
  )}
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  padding: lg;
  width: 100%;
  ${up(
    "tablet",
    css`
      padding: ${"3xl"} lg lg xl;
      width: 50%;
    `
  )}
`;

const Title = styled(Text).attrs({ as: "h2" })`
  font-weight: 800;
  font-size: 24px;
  line-height: 120%;
  margin-bottom: xl;
  ${upMobileWide(css`
    font-size: 42px;
  `)}
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: xl;
  ${upMobileWide(css`
    flex-direction: row;
  `)}
`;

const FindNextJobButton = styled(Button)`
  background-color: ${palette.brand.black};
  border-color: ${palette.brand.black};
  color: ${palette.brand.white};
  font-size: 13px;
  margin-bottom: md;
  margin-right: md;
  margin-top: sm;
  padding: sm lg;
  width: 100%;
  &:hover {
    background-color: ${palette.grayscale.shade600};
    border-color: ${palette.grayscale.shade600};
  }
  ${upMobileWide(css`
    margin-bottom: 0;
    margin-top: 0;
    width: auto;
  `)}
`;

const HeaderImage = styled.img`
  padding: md;
  width: 50%;
  display: none;
  ${up(
    "tablet",
    css`
      display: block;
    `
  )}
`;

const NewProductWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1100px;
`;

const NewProductHeader = styled.div`
  width: 100%;
  padding: 0 ${"3xl"};
  ${upMobileWide(css`
    width: 90%;
  `)}
  ${up(
    "tablet",
    css`
      width: 65%;
    `
  )}
`;

const GetSetUpButton = styled(Button).attrs({ level: "primary" })`
  font-size: 13px;
  margin-right: md;
  margin-top: xl;
  padding: sm lg sm lg;
`;

const NewProductContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: xxl;
  ${upMobileWide(css`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    width: 100%;
    padding: 0 ${"3xl"};
    margin-top: ${"3xl"};
  `)}
`;

const ProductImage = styled.img`
  max-height: 160px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: lg;
  ${upMobileWide(css`
    /* 3 per row: subtract the 2 gaps (2 x 20px) then divide by 3 */
    flex: 0 0 calc((100% - 40px) / 3);
    height: auto;
    margin-bottom: 0;
    max-height: none;
    max-width: calc((100% - 40px) / 3);
  `)}
  &:last-child {
    margin-bottom: 0;
  }
`;

const NextOpportunityWrapper = styled.div`
  background-color: ${palette.grayscale.shade50};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  margin: lg;
  max-width: 1100px;
  padding: xxl;
  width: 90%;
  ${up(
    "tablet",
    css`
      margin: xxl;
      padding: xxl ${"5xl"} xxl ${"5xl"};
    `
  )}
`;

const NextOpportunityTitle = styled(Title)`
  max-width: 700px;
`;

const OpportunityButtonWrapper = styled.div`
  display: flex;
  margin-top: xl;
`;

const OpportunityNextJobButton = styled(Button)`
  font-size: 13px;
  padding: sm lg sm lg;
`;

const CardsWrapper = styled.div`
  position: relative;
  width: 640px;
  max-width: 100%;
  margin: ${"4xl"} auto 0 auto;
  display: none;
  ${up(
    "tablet",
    css`
      display: block;
    `
  )}
`;

const StopScrollingWrapper = styled.div`
  position: relative;
  z-index: 2;
  border: 1px solid ${palette.brand.beige};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  width: 300px;
  overflow: hidden;
  transform: rotate(5deg);
`;

const StopScrollingContent = styled.div`
  background-color: ${palette.extended.yellow.shade100};
  padding: lg;
`;

const StopScrollingTitle = styled(Title)`
  font-size: 32px;
  margin-bottom: md;
`;

const CardTopMargin = styled.div`
  background-color: ${palette.brand.white};
  border-bottom: 1px solid ${palette.brand.beige};
  padding: sm;
  min-height: 24px;
  text-align: center;
  font-weight: 600;
`;

const BetterMatchesWrapper = styled.div`
  position: absolute;
  top: 80px;
  right: 20px;
  z-index: 1;
  border: 1px solid ${palette.brand.beige};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: rotate(-5deg);
`;

const BatterMatchesContent = styled.img`
  max-width: 360px;
`;

const Footer = styled(Text).attrs({ size: -1 })`
  margin-top: 0;
  padding: xl xl lg xl;
  text-align: center;
  ${up(
    "tablet",
    css`
      margin-top: 100px;
    `
  )}
`;

export const UpgradeYourJobSearch = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    pushAnalyticsEvent({
      eventName: "Candidate Viewed Upgrade Your Job Search",
      pathname,
    });
  }, []);

  return (
    <PageWrapper>
      <HeaderWrapper>
        <StyledWttjLogo />
        <HeaderContent>
          <HeaderText>
            <Title>
              We're moving, and your job search is getting an upgrade
            </Title>
            <Text size={-1}>
              Smarter matching, more relevant jobs, and an experience built
              around you. You'll need to update your profile and preferences,
              just upload your CV and we'll do the rest.
            </Text>
            <ButtonWrapper>
              <FindNextJobButton
                as={Link}
                to="https://welcometothejungle.com/authenticate/login?source=expansion"
                level="secondary"
                data-analytics-id="reonboarding-start"
              >
                Find my next job
              </FindNextJobButton>
              <Button
                as={Link}
                to="https://go.welcometothejungle.com/candidate-migration"
                level="secondary"
                style={{ padding: "8px 12px 8px 12px", fontSize: "13px" }}
                data-analytics-id="reonboarding-read-more"
              >
                Read more about what's new
              </Button>
            </ButtonWrapper>
          </HeaderText>
          <HeaderImage src={Preview} alt="Preview" />
        </HeaderContent>
      </HeaderWrapper>
      <NewProductWrapper>
        <NewProductHeader>
          <Title>Built from everything we've learned.</Title>
          <Text size={-1}>
            This isn't just a new look, it's a smarter platform, built from
            months of feedback, real conversations and years of experience.
          </Text>
          <GetSetUpButton
            as={Link}
            to="https://welcometothejungle.com/authenticate/login?source=expansion"
            level="primary"
            data-analytics-id="reonboarding-start"
          >
            Get set up now
          </GetSetUpButton>
        </NewProductHeader>
        <NewProductContentWrapper>
          <ProductImage src={JobsThatFindYou} />
          <ProductImage src={FasterEverything} />
          <ProductImage src={MoreTransparency} />
          <ProductImage src={ClearerConversations} />
          <ProductImage src={GetApproached} />
        </NewProductContentWrapper>
      </NewProductWrapper>
      <NextOpportunityWrapper>
        <NextOpportunityTitle>
          Ready to find your next opportunity?
        </NextOpportunityTitle>
        <Text size={-1}>
          Join thousands of candidates already experiencing better job matches
          on our new platform.
        </Text>
        <OpportunityButtonWrapper>
          <OpportunityNextJobButton
            as={Link}
            to="https://welcometothejungle.com/authenticate/login?source=expansion"
            level="primary"
            data-analytics-id="reonboarding-start"
          >
            Find my next job
          </OpportunityNextJobButton>
        </OpportunityButtonWrapper>
        <CardsWrapper>
          <StopScrollingWrapper>
            <CardTopMargin />
            <StopScrollingContent>
              <StopScrollingTitle>
                Stop scrolling, start matching.
              </StopScrollingTitle>
              <Text size={-1}>
                Tell us who you are, what you want, and let the magic happen. No
                endless feed, just job recommendations at companies that truly
                match.
              </Text>
            </StopScrollingContent>
          </StopScrollingWrapper>
          <BetterMatchesWrapper>
            <CardTopMargin>Better matches</CardTopMargin>
            <BatterMatchesContent
              src={BetterMatchesImage}
              alt="Better matches image"
            />
          </BetterMatchesWrapper>
        </CardsWrapper>
      </NextOpportunityWrapper>
      <Footer>
        © 2026 Welcome to the Jungle. Questions? Contact our support team.
      </Footer>
    </PageWrapper>
  );
};
